import React, { useEffect, useState } from "react";
import { ScreenHead, DqMarker, useProviders } from "./psPrimitives";
import { VisitTypeEditor } from "./VisitTypeEditor";
import {
  STRUCT, SUGG, useVisitTypes, addVisitType, addSuggested, setListDef, vtMake, seedDemoOverride,
} from "@/lib/visitTypesData";

const IND = "#5856D6";
const off = "text-[var(--gray3)]";

export function VisitTypesScreen({ onOpenDq }) {
  const { vts, listdef } = useVisitTypes();
  const { data: providers = [] } = useProviders();
  const [open, setOpen] = useState(null);

  useEffect(() => { if (providers[0]?.npi_number) seedDemoOverride(providers[0].npi_number); }, [providers]);

  if (open) return <VisitTypeEditor vtId={open} onBack={() => setOpen(null)} onOpenDq={onOpenDq} />;

  const newType = () => { const v = vtMake({ id: "vt" + Date.now(), name: "", abbr: "" }); addVisitType(v); setOpen(v.id); };

  return (
    <div className="fade-up" data-testid="visit-types-screen">
      <ScreenHead eyebrow="Encounter Notes · Optional" title="Visit types"
        lede="How each appointment type is booked, documented and billed. Note templates borrow their structure from here." />

      <div className="flex items-center gap-2 rounded-[10px] px-3 py-2 mb-4 text-[11.5px] font-semibold" style={{ background: "rgba(88,86,214,.06)", color: "#4A4AC2" }}>
        Needs API — the visit type API is in development; nothing here saves.<DqMarker n={60} onOpen={onOpenDq} />
      </div>

      <div className="flex items-center justify-between mb-2">
        <span className="text-[13px] font-bold text-[var(--ink)]">{vts.length} visit types</span>
        <button onClick={newType} data-testid="vt-new" className="text-[12px] font-semibold px-3 py-1.5 rounded-[9px] border border-[#E9E9EC] text-[var(--ink)] hover:bg-black/[.03]">+ New visit type</button>
      </div>

      <div className="rounded-[14px] border border-[#E9E9EC] overflow-hidden bg-white mb-6">
        <div className="grid grid-cols-[1.6fr_1fr_.8fr_.9fr_1.2fr_1.1fr_.8fr] gap-2 px-4 py-2 text-[10px] font-bold uppercase text-[var(--gray2)]" style={{ letterSpacing: ".04em", background: "#F5F5F6" }}>
          <span>Visit type</span><span>Category</span><span>Duration</span><span>Patient</span><span>Note structure</span><span>Overrides</span><span>Status</span>
        </div>
        {vts.map((v, i) => {
          const no = Object.keys(v.overrides).length;
          return (
            <button key={v.id} onClick={() => setOpen(v.id)} data-testid={`vt-row-${v.id}`}
              className="w-full text-left grid grid-cols-[1.6fr_1fr_.8fr_.9fr_1.2fr_1.1fr_.8fr] gap-2 px-4 py-2.5 items-center text-[12.5px] hover:bg-black/[.03] transition" style={{ borderTop: i ? "1px solid #EDEDF0" : undefined }}>
              <span className="min-w-0"><b className="text-[var(--ink)]">{v.name || "Untitled"}</b>{v.abbr ? <span className="ml-1.5 text-[var(--gray3)]">{v.abbr}</span> : null}</span>
              <span className="text-[var(--gray1)]">{v.cat}</span>
              <span className="text-[var(--gray1)]">{v.dur ? v.dur + " min" : <span className={off}>—</span>}</span>
              <span className="text-[var(--gray1)]">{v.pt}</span>
              <span className="text-[var(--gray1)]">{v.note ? STRUCT[v.struct][0] : <span className={off}>No note</span>}</span>
              <span className="text-[var(--gray1)]">{no ? `${no} provider override${no > 1 ? "s" : ""}` : <span className={off}>—</span>}</span>
              <span className={v.status === "Enabled" ? "text-[var(--ink)]" : off}>{v.status}</span>
            </button>
          );
        })}
      </div>

      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-[13px] font-bold text-[var(--ink)]">Suggested for your specialties</span>
          <span className="text-[11px] text-[var(--gray3)]">From your providers’ taxonomies</span>
        </div>
        <div className="flex gap-2 flex-wrap">
          {SUGG.map(([n, t]) => (
            <button key={n} title={t} onClick={() => addSuggested(n)} data-testid={`vt-sugg-${n}`}
              className="text-[12px] font-semibold px-3 py-1.5 rounded-full border border-dashed text-[var(--ink2)] hover:bg-[rgba(88,86,214,.06)] transition" style={{ borderColor: "#C9C9D0" }}>+ {n}</button>
          ))}
        </div>
      </div>

      <div>
        <span className="text-[13px] font-bold text-[var(--ink)] block mb-2">Across all visit types</span>
        <div className="rounded-[14px] border border-[#E9E9EC] bg-white p-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block"><span className="block text-[11px] font-semibold text-[var(--gray2)] mb-1">Default for unmatched imports</span>
              <select value={listdef.imports} onChange={(e) => setListDef({ imports: e.target.value })} data-testid="vt-listdef-imports" className="w-full rounded-[9px] border border-black/[.14] bg-white px-2.5 py-2 text-[12.5px]">
                {vts.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></label>
            <label className="block"><span className="block text-[11px] font-semibold text-[var(--gray2)] mb-1">Default phone encounter</span>
              <select value={listdef.phone} onChange={(e) => setListDef({ phone: e.target.value })} data-testid="vt-listdef-phone" className="w-full rounded-[9px] border border-black/[.14] bg-white px-2.5 py-2 text-[12.5px]">
                <option value="">None</option>
                {vts.map((v) => <option key={v.id} value={v.id}>{v.name}</option>)}
              </select></label>
          </div>
          <p className="text-[11px] text-[var(--gray2)] mt-2.5">An imported appointment whose visit type matches no import name gets the default above.</p>
        </div>
      </div>
    </div>
  );
}
